import { useState, useCallback } from 'react'
import { useAuth, useExecutionReportContext } from '../context' // Adjust path if needed

/**
 * Custom hook to manage actions related to Pauta (checklist) items,
 * including changing item state (checked/unchecked) and opening related dialogs.
 *
 * @param {object} report - The full report object.
 * @param {boolean} canEdit - Indicates if the user has general edit permissions.
 * @param {function} setIndexActivity - Setter for the index of the active activity.
 * @returns {{
 *   item: object | null,
 *   index: number | null,
 *   openDialog1: boolean,
 *   openDialogBacklog: boolean,
 *   openItemDialog: (item: object, index: number) => void,
 *   openItemBacklogMessage: (item: object, index: number) => void,
 *   closeItemDialog: () => void,
 *   closeItemBacklogDialog: (element: object, index: number, state: boolean) => Promise<void>,
 *   changeItemState: (element: object, number: number, page: string) => Promise<void>
 * }}
 */
export const usePautaItemActions = (report, canEdit, setIndexActivity) => {
    const { userData } = useAuth()
    const { saveItem, indexGroup } = useExecutionReportContext()

    const [item, setItem] = useState(null)
    const [index, setIndex] = useState(null)
    const [openDialog1, setOpenDialog1] = useState(false) // For ReportDataDialog
    const [openDialogBacklog, setOpenDialogBacklog] = useState(false) // For BacklogMessageDialog

    // Dialog for Item Data (ReportDataDialog)
    const openItemDialog = useCallback((itemData, itemIndex) => {
        setIndex(itemIndex)
        setItem(itemData)
        setOpenDialog1(true)
        if (setIndexActivity) setIndexActivity(itemIndex)
    }, [setIndexActivity])

    const closeItemDialog = useCallback(() => {
        setOpenDialog1(false)
        setItem(null)
        setIndex(null)
    }, [])

    // Dialog for Backlog Message (BacklogMessageDialog)
    const openItemBacklogMessage = useCallback((itemData, itemIndex) => {
        setIndex(itemIndex)
        setItem(itemData)
        setOpenDialogBacklog(true)
        if (setIndexActivity) setIndexActivity(itemIndex)
    }, [setIndexActivity])

    const closeItemBacklogDialog = useCallback(async (element, itemIndex, state) => {
        setOpenDialogBacklog(false)
        if (state) {
            // Assuming saveItem needs original index (number) and element, and indexGroup
            await saveItem(itemIndex, element, indexGroup)
        }
        setItem(null)
        setIndex(null)
    }, [saveItem, indexGroup])

    // Main Item State Change Logic (Check/Uncheck)
    const changeItemState = useCallback(async (element, number, page) => {
        const isCurrentlyDone = element.isChecked || element.isWarning
        const isChecking = !isCurrentlyDone

        const hasImage = element.messages?.some(msg => msg.urlBase64 || msg.url)
        const hasComment = element.messages?.some(msg => msg.content && msg.content.trim() !== '' && msg.content !== 'Se indica estado ejecutado sin dejar mensajes')

        if (isChecking) {
            if (element.isRequerido && !hasComment) {
                alert('Actividad requiere que deje un comentario.')
                return
            }
            if (element.imagen && !hasImage) {
                alert('Actividad requiere que tome al menos una imágen.')
                return
            }
            if (element.unidad !== '*' && (element.unidadData === null || element.unidadData === undefined || element.unidadData === '')) {
                alert('Actividad requiere que deje una cantidad de insumo utilizado.')
                return
            }

            if (!confirm('¿Desea marcar la tarea como ejecutada?')) {
                return
            }
        } else {
            if (!confirm('Confirme que desea desmarcar la tarea')) {
                return
            }
        }

        const updatedItemToSend = { ...element }

        updatedItemToSend.isChecked = isChecking
        updatedItemToSend.isWarning = false

        if (isChecking && !hasComment) {
            updatedItemToSend.messages = [
                ...(element.messages || []).filter(msg => msg.content !== 'Se indica estado ejecutado sin dejar mensajes'),
                {
                    content: 'Se indica estado ejecutado sin dejar mensajes',
                    id: Date.now(),
                    name: `${userData.name} ${userData.lastName}`,
                    namePicture: null,
                    urlBase64: undefined,
                    user: userData._id
                }
            ]
        } else if (!isChecking) {
            updatedItemToSend.messages = (element.messages || []).filter(msg => msg.content !== 'Se indica estado ejecutado sin dejar mensajes')
        }

        try {
            await saveItem(number, updatedItemToSend, page)
        } catch (error) {
            console.error('Error in changeItemState:', error)
        }
    }, [saveItem, indexGroup, userData])

    return {
        item,
        index,
        openDialog1,
        openDialogBacklog,
        openItemDialog,
        openItemBacklogMessage,
        closeItemDialog,
        closeItemBacklogDialog,
        changeItemState
    }
}
